import {
  balancesByAccount,
  live,
  type Account,
  type AccountBalances,
  type Category,
  type MasterCategory,
  type Snapshot,
} from "@budgeter/shared";

export interface AccountWithBalance extends Account {
  balance: AccountBalances;
}

const zero: AccountBalances = { cleared: 0, uncleared: 0, working: 0 };

/** Open accounts with balances, split on/off budget, in sidebar order. */
export function accountsWithBalances(s: Snapshot): {
  onBudget: AccountWithBalance[];
  offBudget: AccountWithBalance[];
  closed: AccountWithBalance[];
} {
  const balances = balancesByAccount(s.transactions.values());
  const all = live(s, "accounts")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map((a) => ({ ...a, balance: balances.get(a.id) ?? zero }));
  return {
    onBudget: all.filter((a) => !a.closed && a.onBudget),
    offBudget: all.filter((a) => !a.closed && !a.onBudget),
    closed: all.filter((a) => a.closed),
  };
}

export interface CategoryGroup {
  master: MasterCategory;
  categories: Category[];
}

/** Master categories with their categories, sorted. Hidden ones are included unless excluded. */
export function categoryGroups(
  s: Snapshot,
  options: { includeHidden?: boolean } = {},
): CategoryGroup[] {
  const cats = live(s, "categories");
  return live(s, "masterCategories")
    .filter((m) => options.includeHidden || !m.hidden)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map((master) => ({
      master,
      categories: cats
        .filter((c) => c.masterCategoryId === master.id && (options.includeHidden || !c.hidden))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    }));
}

export function payeeName(s: Snapshot, id: string | undefined): string {
  return (id && s.payees.get(id)?.name) || "";
}

export function categoryName(s: Snapshot, id: string | undefined): string {
  return (id && s.categories.get(id)?.name) || "";
}

export function accountName(s: Snapshot, id: string | undefined): string {
  return (id && s.accounts.get(id)?.name) || "";
}
